import {useQuery} from '@tanstack/react-query';
import type {RoadmapFacetsResponse, RoadmapNavigationResponse} from 'client';
import {useEffect, useMemo, useState} from 'react';
import {fetchRoadmapFacets, fetchRoadmapNavigation} from 'views/roadmap/api';
import {resolveRoadmapNavigation} from 'views/roadmap/navigation';
import {buildRoadmapSearchParams, getRoadmapSearchState} from 'views/roadmap/queryParams';
import {Roadmap} from 'views/roadmap/Roadmap';
import {getCurrentQuarterLabel} from 'views/roadmap/roadmapData';
import {RoadmapLoadingSkeleton} from 'views/roadmap/RoadmapLoadingSkeleton';
import {
  RoadmapSearchParamsProvider,
  useMemorySearchParams,
} from 'views/roadmap/roadmapSearchParams';
import {getRequestedOrCurrentRoadmapTimeframeKey} from 'views/roadmap/timeframeQuarter';

/** What the workstreams left rail has selected, expressed for the Roadmap. */
export type RoadmapTarget =
  | {kind: 'company'}
  | {kind: 'my'}
  | {kind: 'person'; ldap: string}
  | {kind: 'pillar'; name: string}
  | {kind: 'workstream'; id: number; pillar?: string | null};

// Same keys/options as Roadmap's own queries so the cache is shared.
const ROADMAP_QUERY_OPTIONS = {staleTime: 60_000, gcTime: 5 * 60_000};

const normalize = (value?: string | null) => (value ?? '').trim().toLocaleLowerCase();

const findPillarScopeKey = (
  facets: RoadmapFacetsResponse,
  timeframeKey: string | null,
  pillar: string
): string | null => {
  const roots =
    facets.timeframe_scopes?.find((group) => group.timeframe_key === timeframeKey)?.scopes ?? [];
  const match = roots.find((root) => {
    const reference = root.companyos_reference;
    if (reference && 'pillar' in reference && normalize(reference.pillar) === normalize(pillar)) {
      return true;
    }
    return normalize(root.label) === normalize(pillar);
  });
  return match?.root_node_key ?? null;
};

// The Roadmap can only select scope roots and their menu children, so a deeper
// workstream resolves to its nearest selectable ancestor.
const findWorkstreamScopeKey = (
  facets: RoadmapFacetsResponse,
  navigation: RoadmapNavigationResponse,
  timeframeKey: string | null,
  workstreamId: number
): string | null => {
  const selectable = new Set(
    resolveRoadmapNavigation(facets, navigation, timeframeKey, null).scopes.map((s) => s.key)
  );
  const nodesByKey = new Map((navigation.nodes ?? []).map((node) => [node.key, node]));
  let node = (navigation.nodes ?? []).find((candidate) => candidate.workstream_id === workstreamId);
  const seen = new Set<string>();
  while (node && !seen.has(node.key)) {
    if (selectable.has(node.key)) return node.key;
    seen.add(node.key);
    node = node.parent_key ? nodesByKey.get(node.parent_key) : undefined;
  }
  return null;
};

/**
 * /workstreams?extra=1: the real Roadmap (minus its scope sidebar)
 * driven by the workstreams left rail. Roadmap state is held in memory so it
 * never touches the host page's URL.
 */
export const EmbeddedRoadmap = ({target}: {target: RoadmapTarget}) => {
  const searchParamsTuple = useMemorySearchParams();
  const [searchParams, setSearchParams] = searchParamsTuple;
  const {requestedTimeframe, requestedQuarter} = getRoadmapSearchState(searchParams);

  const facetsQuery = useQuery({
    queryKey: ['roadmap', 'facets'],
    queryFn: fetchRoadmapFacets,
    ...ROADMAP_QUERY_OPTIONS,
  });
  const timeframeKey = facetsQuery.data
    ? (resolveRoadmapNavigation(
        facetsQuery.data,
        null,
        getRequestedOrCurrentRoadmapTimeframeKey({
          timeframes: facetsQuery.data.timeframes ?? [],
          requestedTimeframe,
          requestedQuarter,
          currentQuarter: getCurrentQuarterLabel(),
        }),
        null
      ).timeframe?.key ?? null)
    : null;
  const needsNavigation = target.kind === 'workstream';
  const navigationQuery = useQuery({
    queryKey: ['roadmap', 'navigation', timeframeKey],
    queryFn: () => fetchRoadmapNavigation(timeframeKey),
    enabled: needsNavigation && Boolean(facetsQuery.data),
    ...ROADMAP_QUERY_OPTIONS,
  });

  // Roadmap params for the target, or null while the lookup data is loading.
  const scopeUpdates = useMemo((): Record<string, string | null> | null => {
    const base = {
      scope: null,
      person: null,
      team: null,
      audience: null,
      group: null,
      narrative: null,
      auditNarrative: null,
    };
    const company = {...base, audience: 'company'};
    switch (target.kind) {
      case 'company':
        return company;
      case 'my':
        return {...base, audience: 'my'};
      case 'person':
        return {...base, person: target.ldap};
      case 'pillar': {
        if (!facetsQuery.data) return facetsQuery.isError ? company : null;
        const key = findPillarScopeKey(facetsQuery.data, timeframeKey, target.name);
        return key ? {...base, scope: key} : company;
      }
      case 'workstream': {
        if (!facetsQuery.data) return facetsQuery.isError ? company : null;
        if (!navigationQuery.data) return navigationQuery.isError ? company : null;
        const key =
          findWorkstreamScopeKey(facetsQuery.data, navigationQuery.data, timeframeKey, target.id) ??
          (target.pillar
            ? findPillarScopeKey(facetsQuery.data, timeframeKey, target.pillar)
            : null);
        return key ? {...base, scope: key} : company;
      }
    }
  }, [
    target,
    facetsQuery.data,
    facetsQuery.isError,
    navigationQuery.data,
    navigationQuery.isError,
    timeframeKey,
  ]);

  const scopeUpdatesKey = scopeUpdates ? JSON.stringify(scopeUpdates) : null;
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!scopeUpdates || !scopeUpdatesKey) return;
    setSearchParams((current) => buildRoadmapSearchParams(current, scopeUpdates));
    setAppliedKey(scopeUpdatesKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeUpdatesKey, setSearchParams]);

  return (
    <RoadmapSearchParamsProvider value={searchParamsTuple}>
      {/* Skeleton only until the first scope lands; later target changes update in place. */}
      {appliedKey !== null ? <Roadmap embedded /> : <RoadmapLoadingSkeleton embedded />}
    </RoadmapSearchParamsProvider>
  );
};
